import { TableCell, TableRow } from '@carbon/react';
import { CheckmarkFilled, CloseFilled } from '@carbon/react/icons';
import { InterestComparison } from './types';
import { ComparisonMapThumbnail } from './comparison-map-thumbnail';
import { FloatingTooltip } from 'components/floating-tooltip';
import { Link } from 'components/link';
import { Date } from 'components/date';
import { snakeToCapitalCase } from 'components/json-renderer/utils';
import { format8, formatNumber } from 'utils/number';
import {
  PERCENTAGE_DIFFERENCE_DECIMAL_PLACES,
  SHAPE_SIMILARITY_DECIMAL_PLACES,
} from './constants';
import style from './reconciler.module.scss';

type ShapeCombinationRowProps = {
  shape: {
    shapeIds: number[];
    workspaceIds: number[];
    shapeNames: string;
    interestComparisons: InterestComparison[];
  };
  geometryName: string;
  workspaces: Record<number, { name: string; lastCalculated: string | null }>;
};

const calculatePercentageDifference = (
  difference: number,
  validatorValue: number
): string => {
  if (validatorValue === 0) {
    return 'N/A (validator value is 0)';
  }
  const percentage = (difference / validatorValue) * 100;
  return `${formatNumber(
    percentage,
    PERCENTAGE_DIFFERENCE_DECIMAL_PLACES,
    PERCENTAGE_DIFFERENCE_DECIMAL_PLACES,
    'decimal',
    false
  )}%`;
};

const formatShapeSimilarity = (percentage: number): string => {
  return `${formatNumber(
    percentage * 100,
    SHAPE_SIMILARITY_DECIMAL_PLACES,
    SHAPE_SIMILARITY_DECIMAL_PLACES,
    'decimal',
    false
  )}%`;
};

export const ShapeCombinationRow = ({
  shape,
  geometryName,
  workspaces,
}: ShapeCombinationRowProps) => {
  const firstComparison = shape.interestComparisons[0];
  const hasGeometry =
    firstComparison?.validatorGeometry || firstComparison?.shapeGeometries;

  const workspaceId = shape.workspaceIds[0];
  const workspace = workspaces[workspaceId];

  return (
    <>
      {shape.interestComparisons.map((comparison, idx) => (
        // Key includes geometry + shape context to ensure uniqueness even if
        // the same interestType appears in multiple shape combinations
        <TableRow
          key={`${geometryName}-${shape.shapeIds.join('-')}-${
            comparison.interestType
          }`}
          className={comparison.match ? style.matchedRow : style.failedRow}
        >
          {idx === 0 && (
            <>
              <TableCell
                {...({ rowSpan: shape.interestComparisons.length } as any)}
              >
                {hasGeometry && (
                  <div className={style.leftColumnMap}>
                    <ComparisonMapThumbnail
                      validatorGeometry={firstComparison.validatorGeometry}
                      shapeGeometries={firstComparison.shapeGeometries}
                      geometryName={geometryName}
                      shapeNames={shape.shapeNames.split(' + ')}
                    />
                  </div>
                )}
              </TableCell>
              <TableCell
                {...({ rowSpan: shape.interestComparisons.length } as any)}
              >
                <div>{shape.shapeNames}</div>
                <div className={style.workspaceIds}>
                  Workspace:{' '}
                  <Link
                    to={`/title_workspaces/${workspaceId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {workspace?.name || workspaceId}
                  </Link>
                </div>
                <div className={style.shapeIds}>
                  Last Calculated:{' '}
                  <Date
                    date={workspace?.lastCalculated}
                    format="lll"
                    showTime
                    fallback="N/A"
                  />
                </div>
                <div className={style.shapeIds}>
                  Shape IDs: {shape.shapeIds.join(', ')}
                </div>
              </TableCell>
              <TableCell
                {...({ rowSpan: shape.interestComparisons.length } as any)}
                className={style.numberCell}
              >
                <span className={style.valueWithIcon}>
                  {firstComparison.shapeMatch ? (
                    <CheckmarkFilled className={style.validIcon} />
                  ) : (
                    <CloseFilled className={style.invalidIcon} />
                  )}
                  <span>
                    {formatShapeSimilarity(
                      firstComparison.shapeOverlapPercentage
                    )}
                  </span>
                </span>
              </TableCell>
            </>
          )}
          <TableCell className={style.interestTypeCell}>
            {snakeToCapitalCase(comparison.interestType)}
          </TableCell>
          <TableCell className={style.numberCell}>
            {format8(comparison.etaValue)}
          </TableCell>
          <TableCell className={style.numberCell}>
            {format8(comparison.validatorValue)}
          </TableCell>
          <TableCell className={style.numberCell}>
            <FloatingTooltip
              content={calculatePercentageDifference(
                comparison.difference,
                comparison.validatorValue
              )}
              placement="top"
            >
              <span className={style.valueWithIcon}>
                {comparison.match ? (
                  <CheckmarkFilled className={style.validIcon} />
                ) : (
                  <CloseFilled className={style.invalidIcon} />
                )}
                <span>{format8(comparison.difference)}</span>
              </span>
            </FloatingTooltip>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
};
